import { useEffect, useRef } from "react";
import { doRefresh, forceLogout } from "../api/client";

/**
 * Session manager — keeps the user logged in while active, logs out after idle.
 *
 * - Every REFRESH_INTERVAL (60s), if the user was active in that interval,
 *   calls /auth/refresh to get a new access token (sliding window).
 * - If the user has been idle (no keyboard/mouse/touch) for IDLE_TIMEOUT
 *   (60 min), forces logout.
 * - Also refreshes on visibility change (tab becomes visible again).
 */

const REFRESH_INTERVAL = 60_000; // 1 minute
const IDLE_TIMEOUT = 60 * 60_000; // 60 minutes

export function useSessionManager() {
  const lastActivity = useRef<number>(Date.now());
  const wasActive = useRef<boolean>(false);

  useEffect(() => {
    // Only run when logged in
    const token = localStorage.getItem("lk_access");
    if (!token) return;

    const onActivity = () => {
      lastActivity.current = Date.now();
      wasActive.current = true;
    };

    // Track user activity
    const events = ["keydown", "mousemove", "click", "scroll", "touchstart"];
    // Throttle mousemove — it fires a lot
    let lastMove = 0;
    const onMouseMove = () => {
      const now = Date.now();
      if (now - lastMove > 10_000) {
        lastMove = now;
        onActivity();
      }
    };
    const onKeydown = () => onActivity();
    const onOther = () => onActivity();

    events.forEach((ev) => {
      if (ev === "mousemove") window.addEventListener("mousemove", onMouseMove);
      else if (ev === "keydown") window.addEventListener("keydown", onKeydown);
      else window.addEventListener(ev, onOther);
    });

    // On tab visible again, refresh if was active
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        const idleMs = Date.now() - lastActivity.current;
        if (idleMs < IDLE_TIMEOUT) {
          wasActive.current = true;
        }
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    // Periodic check
    const interval = setInterval(async () => {
      const idleMs = Date.now() - lastActivity.current;

      if (idleMs >= IDLE_TIMEOUT) {
        // User has been idle for too long — log out
        forceLogout();
        return;
      }

      if (wasActive.current) {
        // User was active in the last interval — refresh the token
        wasActive.current = false;
        try {
          await doRefresh();
        } catch {
          forceLogout();
        }
      }
    }, REFRESH_INTERVAL);

    return () => {
      events.forEach((ev) => {
        if (ev === "mousemove") window.removeEventListener("mousemove", onMouseMove);
        else if (ev === "keydown") window.removeEventListener("keydown", onKeydown);
        else window.removeEventListener(ev, onOther);
      });
      document.removeEventListener("visibilitychange", onVisibility);
      clearInterval(interval);
    };
  }, []);
}
